import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Badge, Dropdown } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import { RootState } from "../../../../globals";
import HttpManager from "../../../../services/HttpManager";
import Card from "../../../../components/card";
import { cursoCorto } from "../../../../utils/cursoCorto";
import { telefonoBonito } from "../../../../utils/telefonoBonito";
import useEsTelefono from "../../../../utils/esTelefono";
import { horarioBonito } from "../../../../utils/horarioBonito";
import PaginationComponent from "../../../../components/pagination/pagination";
import SearchInputComponent from "../../../../components/searchInput/searchInput";
import { formatLocalDateTime } from "../../../../components/enrollment/format";
import { EnrollmentRequestDetailsDTO, EnrollmentRequestListDTO, EnrollmentRequestSummaryDTO, REQUEST_MODE, REQUEST_STATUS } from "../../../../models/EnrollmentHistoryDTO";
import RequestFormModal from "./requestFormModal";
import RequestDetailsModal from "./requestDetailsModal";
import S from "./strings";
import AccionesAlumno from "../../../../components/enrollment/accionesAlumno";
import { buildPages, errorText, estadoVariant, HORARIO_LISTA } from "./helpers";
import useEnlaceDirecto from "../../../../utils/enlaceDirecto";
import ConfirmarPlantel, { PlantelElegido } from "./confirmarPlantel";
import PreSolicitudesController from "../../sales/preSolicitudes/preSolicitudesController";
import { PrerequestListDTO, PrerequestPermissions } from "../../../../services/v2/enrollmentPrerequests.service";
import ContratosFirma from "./contratosFirma";
import { conTaller } from "./pasoTalleres";
import FiltrosMatriculas, { FILTROS_VACIOS, FiltrosMatriculasValor, OpcionesFiltros, rangoDe } from "./filtrosMatriculas";

/*
	Cuatro bandejas y ninguna más (Esteban, 2026-09-22): "Todas" no existe, y lo que Caja no
	aplicó ya no tiene bandeja propia — vuelve a Rechazadas marcado como rechazado por Caja.
*/
type TabKey = "solicitudes" | "submitted" | "returned" | "drafts" | "pendingPayment" | "enrolled" | "contratos";
const TAB_STATUSES: Record<TabKey, number[]> = {
	solicitudes: [], submitted: [REQUEST_STATUS.ENVIADA], returned: [REQUEST_STATUS.DEVUELTA], drafts: [REQUEST_STATUS.BORRADOR],
	pendingPayment: [], enrolled: [], contratos: []
};
//100 por pantalla (Esteban, 2026-10-20); el servidor no da más de 200.
const PAGE_SIZES = [100, 200];

/**
 * "Solicitudes de matrícula": the shared inbox where Ventas drafts remote sign-ups and Admisiones reviews, returns or approves them.
 * Backend decides visibility (Ventas only sees its own) and returns the permissions used to show buttons.
 */
/** inicial: con qué pestaña abre (el menú viejo "Solicitudes" y los avisos de Cliq de Solicitudes abren en "solicitudes"). */
const EnrollmentRequestsController: React.FC<{ inicial?: TabKey }> = ({ inicial }) => {
	const http = HttpManager.getInstance();
	const movil = useEsTelefono();
	// v2 session campus is set at login; after a plain page refresh fall back to the legacy campus ref so the screen still works.
	const sessionCampus: any = useSelector((state: RootState) => state.rootReducer.sessionState.currentCampus);
	const legacyCampusKey: any = useSelector((state: RootState) => state.currentUserCampus.value);
	const legacyCampusRef: any = useSelector((state: RootState) => state.campusRef.value);
	const legacyCampus: any = legacyCampusKey && legacyCampusRef ? legacyCampusRef[legacyCampusKey] : null;
	/*
		Manda el plantel de la BARRA (el que la persona ve), no el guardado en la sesión: los dos se
		desfasaban y Matrículas abría Monterrey estando en San Pedro Sula; el botón Finanzas llevaba
		el plantel equivocado y movía la barra (Esteban, 2026-09-24). El de la sesión es solo respaldo.
	*/
	const currentCampus: any = legacyCampus
		? { id: legacyCampus.id, countryId: legacyCampus.company?.country_id ?? (sessionCampus?.id === legacyCampus.id ? sessionCampus?.countryId : undefined) }
		: sessionCampus;
	const campusId: number | null = currentCampus?.id ?? null;

	// Primera pestaña "Solicitudes en Proceso" (Esteban, 2026-10-19): ahí abre Ventas; Admisiones abre en Por revisar (ver abajo).
	const usuario: any = useSelector((state: RootState) => state.userProfile.value);
	const permits: any = usuario?.roleMap?.[usuario?.currentRole]?.permits || {};
	const conSolicitudes = !!permits.enrollmentPrerequests;
	// Agente de Ventas (rol 32) tiene Solicitudes pero no Matrículas: ve solo su pestaña.
	const conMatriculas = !!permits.enrollmentRequests;
	const [tab, setTab] = useState<TabKey>(inicial || (conSolicitudes ? "solicitudes" : "submitted"));
	// Ya se eligió la pestaña de arranque (o la persona ya movió las pestañas): no se vuelve a cambiar sola.
	const [arranqueListo, setArranqueListo] = useState<boolean>(!!inicial || !conSolicitudes || !conMatriculas);
	const [infoSolicitudes, setInfoSolicitudes] = useState<{ permisos: PrerequestPermissions; cuentas: PrerequestListDTO["counts"] } | null>(null);
	const [nuevaSolicitudTick, setNuevaSolicitudTick] = useState<number>(0);
	const [permisosMat, setPermisosMat] = useState<EnrollmentRequestListDTO["permissions"] | null>(null);
	// Ya matriculados: el alumno existe y tiene estado de cuenta.
	const conAccesos = tab === "pendingPayment" || tab === "enrolled";
	const [onlyMine, setOnlyMine] = useState<boolean>(false);
	const [yaAjustadoOnlyMine, setYaAjustadoOnlyMine] = useState<boolean>(false);
	const [search, setSearch] = useState<string>("");
	const [appliedSearch, setAppliedSearch] = useState<string>("");
	const [perPage, setPerPage] = useState<number>(PAGE_SIZES[0]);
	const [page, setPage] = useState<number>(1);
	const [result, setResult] = useState<EnrollmentRequestListDTO | null>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [notice, setNotice] = useState<string | null>(null);
	const [form, setForm] = useState<{ requestId: number | null; modeId: number; plantel?: PlantelElegido; bundleWith?: { requestId: number; name: string }; n?: number } | null>(null);
	// Matrícula nueva: primero se confirma el plantel (Esteban, 2026-10-12). Guarda el tipo elegido mientras tanto.
	const [pedirPlantel, setPedirPlantel] = useState<number | null>(null);
	const [detailsId, setDetailsId] = useState<number | null>(null);
	//Enlace de un aviso de Cliq: abre la solicitud en su plantel (2026-09-25).
	const enlace = useEnlaceDirecto("solicitud", (id) => setDetailsId(id));
	const [refreshTick, setRefreshTick] = useState<number>(0);
	const [filtros, setFiltros] = useState<FiltrosMatriculasValor>(FILTROS_VACIOS);
	const [opciones, setOpciones] = useState<OpcionesFiltros | null>(null);
	//La fecha es la de matrícula en las bandejas de matriculados, la de creación en las demás.
	const porMatricula = tab === "pendingPayment" || tab === "enrolled";

	useEffect(() => {
		if (campusId === null || !conMatriculas) {
			return;
		}
		let vigente = true;
		setOpciones(null);
		setFiltros(FILTROS_VACIOS);
		http.enrollmentRequestsService.filterOptions(campusId).then((r) => { if (vigente) setOpciones(r.data); }).catch(() => { /* la barra queda sin opciones */ });
		return () => { vigente = false; };
	}, [campusId]);

	const permissions = result?.permissions || permisosMat || undefined;

	// Permisos y cuenta de Solicitudes aunque se abra en otra pestaña (el botón Nueva y el número de la pestaña).
	useEffect(() => {
		if (campusId === null || !conSolicitudes) {
			return;
		}
		let vigente = true;
		setInfoSolicitudes(null);
		http.enrollmentPrerequestsService.list(campusId, "proceso", "", 1, 1)
			.then((r) => { if (vigente) setInfoSolicitudes({ permisos: r.data.permissions, cuentas: r.data.counts }); })
			.catch(() => { /* sin botón de Solicitud */ });
		return () => { vigente = false; };
	}, [campusId]);

	/*
		Quién es se sabe por los permisos de Matrículas, que llegan con cualquier lista: se piden una vez (una fila) para
		elegir la pestaña de arranque sin esperar a que la persona abra otra. Admisiones (revisa) → Por revisar.
	*/
	useEffect(() => {
		if (campusId === null || !conMatriculas) {
			return;
		}
		let vigente = true;
		http.enrollmentRequestsService.list(campusId, { statusIds: [REQUEST_STATUS.ENVIADA] }, 1, 1)
			.then((r) => {
				if (!vigente) return;
				setPermisosMat(r.data.permissions);
				if (!arranqueListo) {
					if (r.data.permissions?.isRevisor) setTab("submitted");
					setArranqueListo(true);
				}
			})
			.catch(() => { if (vigente) setArranqueListo(true); });
		return () => { vigente = false; };
	}, [campusId]);

	useEffect(() => {
		if (campusId === null || tab === "solicitudes" || tab === "contratos" || !conMatriculas) {
			return;
		}
		setLoading(true);
		setError(null);
		const criteria: any = { statusIds: TAB_STATUSES[tab], search: appliedSearch || undefined, assignedToMe: onlyMine || undefined,
			pendingPayment: tab === "pendingPayment" || undefined, enrolled: tab === "enrolled" || undefined };
		//"Hoy" y "Esta semana" se recalculan al pedir: si la pantalla quedó abierta desde ayer, siguen siendo hoy.
		const fechas = filtros.preset && filtros.preset !== "rango" ? rangoDe(filtros.preset) : { dateFrom: filtros.dateFrom, dateTo: filtros.dateTo };
		if (fechas.dateFrom) {
			criteria.dateField = porMatricula ? "enrolled" : "created";
			criteria.dateFrom = fechas.dateFrom;
			criteria.dateTo = fechas.dateTo || fechas.dateFrom;
		}
		if (filtros.advisors.length) { criteria.advisors = filtros.advisors; }
		if (filtros.groups.length) { criteria.groups = filtros.groups; }
		if (filtros.courseIds.length) { criteria.courseIds = filtros.courseIds; }
		//Solo vale la respuesta de la última petición: al cambiar de plantel la del anterior puede llegar tarde (2026-09-25).
		let vigente = true;
		http.enrollmentRequestsService.list(campusId, criteria, perPage, page)
			.then((r) => { if (vigente) setResult(r.data); })
			.catch((e) => { if (vigente) setError(errorText(e)); })
			.finally(() => { if (vigente) setLoading(false); });
		return () => { vigente = false; };
	}, [campusId, tab, appliedSearch, onlyMine, perPage, page, refreshTick, filtros]);

	/*
		Quien revisa abre viendo solo lo suyo: su bandeja es lo que le toca, no la del plantel
		entero (Esteban, 2026-09-23). La supervisora no tiene asignaciones, así que a ella no.
	*/
	useEffect(() => {
		if (!permissions || yaAjustadoOnlyMine) {
			return;
		}
		setYaAjustadoOnlyMine(true);
		//La asesora de Admisiones ya solo recibe lo suyo desde el servidor (asignado, revisado, creado o
		//comentado por ella): el filtro le escondería su propio historial (Esteban, 2026-09-24).
		if (permissions.isRevisor && !permissions.isSupervisora && !permissions.isAsesoraAdmisiones) {
			setOnlyMine(true);
		}
	}, [permissions]);

	// Ventas no revisa nada: si su bandeja de "Por revisar" está vacía, se le abren sus borradores.
	useEffect(() => {
		if (permissions && permissions.isSupervisora && !permissions.isRevisor && tab === "submitted" && result && result.total === 0 && !appliedSearch) {
			setTab("drafts");
		}
	}, [permissions?.isSupervisora]);

	const refresh = () => setRefreshTick((t) => t + 1);
	const changeTab = (key: TabKey) => { setTab(key); setPage(1); setArranqueListo(true); };
	const pages = useMemo(() => result ? buildPages(result.currentPage, result.lastPage, (p) => setPage(p), movil) : [], [result, movil]);
	const itemCount = result ? {
		start: result.total === 0 ? 0 : (result.currentPage - 1) * result.perPage + 1,
		end: Math.min(result.currentPage * result.perPage, result.total),
		total: result.total
	} : { start: 0, end: 0, total: 0 };

	const onSaved = (details: EnrollmentRequestDetailsDTO, action: "saved" | "submitted" | "approved" | "resent") => {
		setForm(null);
		setNotice(action === "resent" ? S.resentToCaja : action === "approved" ? S.approved : action === "submitted" ? S.submitted : S.saved);
		refresh();
		// En otro plantel: su detalle vive allá (se ve cambiando el plantel de la barra).
		if (!form?.plantel || form.plantel.id === campusId) {
			setDetailsId(details.id);
		} else {
			setNotice((action === "submitted" ? S.submitted : S.saved) + " · " + form.plantel.nombre);
		}
	};

	const enProceso = infoSolicitudes?.cuentas?.inProcess;
	const tabs: { key: TabKey; label: string; cuenta?: number }[] = [
		...(conSolicitudes ? [{ key: "solicitudes" as TabKey, label: "Solicitudes en Proceso", cuenta: enProceso }] : []),
		...(conMatriculas ? [
			{ key: "submitted" as TabKey, label: S.tabs.submitted }, { key: "returned" as TabKey, label: S.tabs.returned },
			// Borradores solo para quien llena matrículas: la vendedora crea Solicitudes, no Matrículas (2026-10-19).
			...(permissions && !permissions.canCreate ? [] : [{ key: "drafts" as TabKey, label: S.tabs.drafts }]),
			{ key: "pendingPayment" as TabKey, label: S.tabs.pendingPayment }, { key: "enrolled" as TabKey, label: S.tabs.enrolled },
			// Contratos (2026-10-15): enviados para firma por WhatsApp, abiertos y firmados.
			{ key: "contratos" as TabKey, label: "Contratos" }
		] : [])
	];
	const creaSolicitud = conSolicitudes && !!infoSolicitudes?.permisos?.canCreate;
	const creaMatricula = conMatriculas && !!permissions?.canCreate;
	const nuevaSolicitud = () => { changeTab("solicitudes"); setNuevaSolicitudTick((t) => t + 1); };
	const opcionesNueva: { texto: string; boton: string; abrir: () => void }[] = [
		...(creaSolicitud ? [{ texto: "Solicitud", boton: "Nueva Solicitud", abrir: nuevaSolicitud }] : []),
		...(creaMatricula && permissions?.canPresencial ? [{ texto: "Matrícula en Persona", boton: S.newRequest, abrir: () => setPedirPlantel(REQUEST_MODE.PRESENCIAL) }] : []),
		...(creaMatricula ? [{ texto: permissions?.canPresencial ? "Matrícula Digital" : "Matrícula", boton: S.newRequest, abrir: () => setPedirPlantel(REQUEST_MODE.REMOTA) }] : [])
	];

	if (campusId === null) {
		return <React.Fragment />;
	}

	return (
		<div className="container-fluid waModulo" style={{ textAlign: "left", padding: movil ? "10px 12px 0 12px" : "10px 24px 0 24px" }}>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 8 }}>
				<h4 style={{ margin: 0 }}>{S.title}</h4>
				<div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
					{/*
						Vendedora: Nueva Solicitud (nunca Matrícula). Quien hace las dos (supervisoras, Admisiones): un solo botón
						Nueva con las opciones; la Solicitud primero porque es lo que más se crea (Esteban, 2026-10-19).
					*/}
					{opcionesNueva.length > 1 ? (
						<Dropdown>
							<Dropdown.Toggle variant="primary" className="waBotonGrande"><FontAwesomeIcon icon={solid("plus")} />&nbsp;Nueva</Dropdown.Toggle>
							<Dropdown.Menu>
								{opcionesNueva.map((o) => <Dropdown.Item key={o.texto} onClick={o.abrir}>{o.texto}</Dropdown.Item>)}
							</Dropdown.Menu>
						</Dropdown>
					) : opcionesNueva.length === 1 ? (
						<button type="button" className="btn btn-primary waBotonGrande" onClick={opcionesNueva[0].abrir}>
							<FontAwesomeIcon icon={solid("plus")} />&nbsp;{opcionesNueva[0].boton}
						</button>
					) : ""}
				</div>
			</div>
			{enlace.aviso ? <div className="alert alert-warning" role="alert" style={{ fontSize: 15, cursor: "pointer" }} onClick={enlace.cerrar}>{enlace.aviso}</div> : ""}
			{error ? <div className="alert alert-danger" style={{ fontSize: 15 }}>{error}</div> : ""}
			{notice ? <div className="alert alert-success" style={{ fontSize: 15, display: "flex", justifyContent: "space-between" }}>{notice}<button type="button" className="btn-close" style={{ fontSize: 10 }} onClick={() => setNotice(null)} /></div> : ""}

			<Card>
				<div style={{ padding: movil ? "12px 12px 0 12px" : "14px 18px 4px 18px" }}>
					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
						<div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
							<ul className="nav nav-pills waTabs" style={{ gap: 4 }}>
								{tabs.map((t) => (
									<li className="nav-item" key={t.key}>
										<button type="button" className={"nav-link btn btn-sm " + (tab === t.key ? "active" : "")} style={{ padding: "4px 12px", fontSize: 15 }} onClick={() => changeTab(t.key)}>
											{t.label}{t.cuenta ? <span className="waTabCuenta">{t.cuenta}</span> : null}
										</button>
									</li>
								))}
							</ul>
							{permissions?.isRevisor && !permissions?.isAsesoraAdmisiones && tab !== "solicitudes" && tab !== "contratos" ? (
								<label style={{ position: "static", fontSize: 15, cursor: "pointer", margin: 0, whiteSpace: "nowrap" }}>
									<input type="checkbox" checked={onlyMine} onChange={(e) => { setOnlyMine(e.target.checked); setPage(1); }} />&nbsp;{S.onlyMine}
								</label>
							) : ""}
						</div>
						{tab !== "contratos" ? (
						<div style={movil ? { flex: "1 1 100%", position: "relative" } : { minWidth: 320, position: "relative" }}>
							<SearchInputComponent search={search} onChangeSearch={setSearch}
								onClickSearch={() => { setAppliedSearch(search.trim()); setPage(1); }}
								onClearSearch={() => { setSearch(""); setAppliedSearch(""); setPage(1); }} />
						</div>
						) : null}
					</div>
					{tab === "contratos" ? <ContratosFirma campusId={campusId} /> : tab === "solicitudes" ? (
						/* Cada cliente de punta a punta; Completar abre el formulario de Matrícula y el número abre su detalle. */
						<PreSolicitudesController busqueda={appliedSearch} nuevaTick={nuevaSolicitudTick} onInfo={setInfoSolicitudes}
							onAbrirMatricula={(id) => setDetailsId(id)} />
					) : !arranqueListo ? <div className="loader" /> : (<>
					<FiltrosMatriculas valor={filtros} opciones={opciones} etiquetaFecha={porMatricula ? "Matriculadas" : "Creadas"}
						onChange={(v) => { setFiltros(v); setPage(1); }} />
					<div className="waScrollX" style={{ overflowX: "auto", marginTop: 18 }}>
						{loading && !result ? <div className="loader" /> : movil ? (
							/*
								En teléfono la tabla de diez columnas obliga a arrastrar de lado, y ellas llenan
								esto desde el celular (Esteban, 2026-09-22). Misma información, en tarjetas.
							*/
							<div style={{ display: "flex", flexDirection: "column", gap: 8, paddingBottom: 8 }}>
								{!result || result.data.length < 1 ? (
									<div style={{ color: "var(--wa-apagado)", textAlign: "center", padding: 24 }}>{S.empty}</div>
								) : result.data.map((r: EnrollmentRequestSummaryDTO) => (
									<div key={"rqm_" + r.id} onClick={() => setDetailsId(r.id)}
										style={{ border: "1px solid var(--wa-filo)", borderRadius: 8, padding: "10px 12px", cursor: "pointer", opacity: loading ? 0.6 : 1 }}>
										<div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>
											<b style={{ fontSize: 15 }}>{r.studentName || "-"}</b>
											<Badge bg={estadoVariant(r.statusId, r.status)} style={{ fontWeight: 500, flexShrink: 0 }}>{r.status}</Badge>
										</div>
										{r.returnedByCaja ? <div className="waOroTexto" style={{ fontSize: 13, fontWeight: 700 }}>{S.returnedByCaja}</div> : ""}
										<div style={{ fontSize: 13, marginTop: 2 }}>{cursoCorto(r.courseName) || (r.workshopName ? conTaller(r.workshopName) : "-")}</div>
										<div style={{ fontSize: 13, color: "var(--wa-apagado)" }}>
											{horarioBonito(r.scheduleName, HORARIO_LISTA) || (r.workshopName ? "Pago único" : "-")}
											{(r.scheduleCount || 1) > 1 ? ` · +${(r.scheduleCount || 1) - 1} ${(r.scheduleCount || 1) > 2 ? "cursos más" : "curso más"}` : ""}
										</div>
										{(r.advisorsShort || []).length > 0 ? (
											<div style={{ fontSize: 13, color: "var(--wa-apagado)" }}>{S.col.advisors}: {(r.advisorsShort || []).join(", ")}</div>
										) : ""}
										<div style={{ fontSize: 13, color: "var(--wa-apagado)", marginTop: 4 }}>
											{S.col.id}{r.id} · {r.mode} · {r.isNewStudent ? "Nuevo" : "Registrado"}
											{r.phone ? " · " + telefonoBonito(r.phone, currentCampus?.countryId) : ""}
										</div>
										<div style={{ fontSize: 13, color: "var(--wa-apagado)" }}>
											{/* Quién la mandó, quién de Admisiones la tiene y quién de Caja, para saber a quién escribirle (2026-09-25). */}
											{S.col.createdBy}: {r.createdByShort || r.createdBy || "-"} · {S.col.assignedTo}: {r.assignedToShort || r.assignedTo || "-"}
											{r.cajaShort ? <> · {S.col.caja}: {r.cajaShort}</> : ""} · {formatLocalDateTime(r.updatedAt)}
										</div>
										{conAccesos && r.studentId ? <div style={{ marginTop: 8 }}><AccionesAlumno studentId={r.studentId} campusId={campusId} alinear="inicio" /></div> : ""}
									</div>
								))}
							</div>
						) : (
							/* Anchos chicos y textos que se parten: con el menú fijo la tabla tiene ~900 px y antes pedía ~1380 (Esteban, 2026-10-22). */
							<table className="table table-hover" style={{ fontSize: 15, marginBottom: 0 }}>
								<thead>
									<tr>
										<th style={{ width: 48, textAlign: "center" }}>{S.col.id}</th>
										<th style={{ width: 190 }}>{S.col.student}</th>
										<th style={{ width: 200 }}>{S.col.course}</th>
										<th style={{ width: 120 }}>{S.col.advisors}</th>
										<th style={{ width: 80 }}>{S.col.mode}</th>
										{/* 1% + sin partir = tan ancha como la etiqueta más larga de la página, ni un pixel más. */}
										<th style={{ width: "1%", whiteSpace: "nowrap" }}>{S.col.status}</th>
										<th style={{ width: 100 }}>{S.col.createdBy}</th>
										<th style={{ width: 100 }}>{S.col.assignedTo}</th>
										<th style={{ width: 90 }}>{S.col.caja}</th>
										<th style={{ width: 100 }}>{S.col.updatedAt}</th>
										{/*
											Única columna sin ancho: se queda con el sobrante de la pantalla, así ninguna otra
											se estira (Curso / Horario y luego Estado salían enormes). En Pendiente Pago y
											Finalizadas lleva Ver perfil y Finanzas, como en Estudiantes (Esteban, 2026-09-23).
										*/}
										<th style={{ paddingRight: 18 }} />
									</tr>
								</thead>
								<tbody>
									{!result || result.data.length < 1 ? (
										<tr><td colSpan={11} style={{ color: "var(--wa-apagado)", textAlign: "center", padding: 24 }}>{S.empty}</td></tr>
									) : result.data.map((r: EnrollmentRequestSummaryDTO) => (
										<tr key={"rq_" + r.id} style={{ cursor: "pointer", opacity: loading ? 0.6 : 1 }} onClick={() => setDetailsId(r.id)}>
											<td style={{ whiteSpace: "nowrap", textAlign: "center" }}>#{r.id}</td>
											<td>
												<b>{r.studentName || "-"}</b>
												<div style={{ fontSize: 13, color: "var(--wa-apagado)" }}>{r.isNewStudent ? "Nuevo" : "Registrado"}{r.phone ? ` · ${telefonoBonito(r.phone, currentCampus?.countryId)}` : ""}</div>
											</td>
											<td>
												{cursoCorto(r.courseName) || (r.workshopName ? conTaller(r.workshopName) : "-")}
												<div style={{ fontSize: 13, color: "var(--wa-apagado)" }}>{horarioBonito(r.scheduleName, HORARIO_LISTA) || (r.workshopName ? "Pago único" : "-")}</div>
												{(r.scheduleCount || 1) > 1 ? (
													<div style={{ fontSize: 13, color: "var(--wa-apagado)" }}>
														+{(r.scheduleCount || 1) - 1} {(r.scheduleCount || 1) > 2 ? "cursos más" : "curso más"}
													</div>
												) : ""}
											</td>
											{/* Una asesora por renglón: dos nombres juntos hacían la columna el doble de ancha. */}
											<td>{(r.advisorsShort || []).length > 0
												? (r.advisorsShort || []).map((nombre, i) => <div key={"as_" + i} style={{ whiteSpace: "nowrap" }}>{nombre}</div>)
												: "-"}</td>
											<td style={{ whiteSpace: "nowrap" }}>{r.mode}</td>
											<td style={{ whiteSpace: "nowrap" }}>
												<Badge bg={estadoVariant(r.statusId, r.status)} style={{ fontWeight: 500 }}>{r.status}</Badge>
												{r.returnedByCaja ? <div className="waOroTexto" style={{ fontSize: 13, fontWeight: 700, marginTop: 2 }}>{S.returnedByCaja}</div> : ""}
											</td>
											<td>{r.createdByShort || r.createdBy || "-"}</td>
											<td>{r.assignedToShort || r.assignedTo || "-"}</td>
											<td>{r.cajaShort || "-"}</td>
											<td>{formatLocalDateTime(r.updatedAt)}</td>
											<td className="celdaAcciones" style={{ paddingRight: 18 }}>
												{conAccesos && r.studentId ? <AccionesAlumno studentId={r.studentId} campusId={campusId} /> : ""}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						)}
					</div>
					</>)}
				</div>
			</Card>
			{tab !== "solicitudes" && tab !== "contratos" ? (
			<div className="waSobreDock" style={{ marginTop: 10 }}>
				<PaginationComponent pages={pages} pageSizes={PAGE_SIZES} itemCount={itemCount} onPageSizeChange={(size) => { setPerPage(size); setPage(1); }} />
			</div>
			) : null}
			{pedirPlantel !== null ? (
				<ConfirmarPlantel que="matrícula" sugerido={campusId} onCerrar={() => setPedirPlantel(null)}
					onElegir={(p) => { setForm({ requestId: null, modeId: pedirPlantel, plantel: p }); setPedirPlantel(null); }} />
			) : null}

			{form ? (
				<RequestFormModal key={"f" + (form.requestId ?? "n") + "-" + (form.n || 0)} campusId={form.plantel?.id || campusId} countryId={form.plantel?.countryId || currentCampus.countryId} requestId={form.requestId} initialModeId={form.modeId}
					canPresencial={!!permissions?.canPresencial} esAdmisiones={!!permissions?.isRevisor} onClose={() => { setForm(null); refresh(); }} onSaved={onSaved}
					bundleWith={form.bundleWith || null}
					onSwitch={(t) => { refresh(); setForm({ requestId: t.requestId, modeId: form.modeId, plantel: form.plantel, bundleWith: t.bundleWith, n: (form.n || 0) + 1 }); }} />
			) : ""}
			{detailsId !== null && !form ? (
				<RequestDetailsModal campusId={campusId} countryId={currentCampus?.countryId} requestId={detailsId} onClose={() => setDetailsId(null)} onChanged={refresh}
					onEdit={(id) => { setDetailsId(null); setForm({ requestId: id, modeId: REQUEST_MODE.REMOTA }); }} />
			) : ""}
		</div>
	);
};

export default EnrollmentRequestsController;
